import {Injectable} from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  CanLoad,
  Route,
  Router,
  RouterStateSnapshot,
  UrlSegment,
  UrlTree
} from '@angular/router';
import {Observable, throwError} from 'rxjs';
import {catchError, map, take} from 'rxjs/operators';
import {AuthService} from './auth.service';
import {ApiService} from './api.service';
import {CommonUtils} from "../utils/common";

/**
 * Guard zarządzający dostępem do modułu zarządzania pojedynczymi
 * placówkami weterynaryjnymi (VetClinic).
 * Weryfikuje po stronie serwera API, czy aktualnie zalogowany użytkownik posiada
 * wymagane uprawnienia (RBAC) do przeglądania i edycji danych na poziomie danej placówki.
 */
@Injectable({
  providedIn: 'root'
})
export class ClinicsAccessGuard implements CanLoad, CanActivate {
  constructor(
    private authService: AuthService,
    private router: Router,
    private apiService: ApiService,
    public commonUtils: CommonUtils
  ) {
  }

  /**
   * Blokuje lub zezwala na asynchroniczne pobranie (Lazy Loading) paczki modułu lecznic z serwera.
   * Funkcja ochronna optymalizująca bezpieczeństwo i transfer danych – weryfikuje dostęp
   * poprzez zapytanie POST przed inicjalizacją pobierania kodu JavaScript modułu.
   *
   * @param route Konfiguracja chronionej ścieżki.
   * @param segments Segmenty adresu URL.
   * @returns Observable emitujący wartość logiczną (true w przypadku posiadania odpowiednich ról).
   */
  canLoad(
    route: Route,
    segments: UrlSegment[]
  ): Observable<boolean> | Promise<boolean> | boolean {
    return this.apiService.post('/dashboard/clinics-access', {}).pipe(take(1),
      catchError(err => {
        if (err.status === 401) {
          this.router.navigate(['signin']).then(() => {
            this.authService.logout();
          });
        }
        return throwError(err);
      })
      , map(response => {
        console.log('check - clinics - ok');
        return true;
      }));
  }

  /**
   * Weryfikuje uprawnienia przed fizycznym wyrenderowaniem (aktywacją) komponentu
   * powiązanego z zarządzaniem kliniką. Przechwytuje błędy braku sesji (401), wymuszając wylogowanie.
   * W przypadku odmowy dostępu z powodów innych niż brak sesji, deleguje wyświetlenie
   * odpowiedniego komunikatu do narzędzi z klasy CommonUtils.
   *
   * @param route Migawka aktywowanej ścieżki.
   * @param state Migawka stanu routera.
   * @returns Observable emitujący wartość logiczną lub drzewo routingu (UrlTree) określające wynik nawigacji.
   */
  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    return this.apiService.post('/dashboard/clinics-access', {}).pipe(take(1), catchError(err => {
      if (err.status === 401) {
        this.router.navigate(['signin']).then(() => {
          this.authService.logout();
        });
      }
      return throwError(err);
    }), map(response => {
      console.log('check - canActivate - clinics - ok', response);

      return this.commonUtils.handleHttpResponseShowInfoToast(response, '', 'Nie masz uprawnień do tej strony')
    }));
  }

}
